"use client";

import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Loader2, ChevronDown, MessageSquare } from "lucide-react";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { useGetOrdersQuery } from "@/features/api/paymentApi";
import { useNavigate } from "react-router-dom";

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const { user: authUser } = useSelector((state) => state.auth);

  const { data, isLoading, error } = useGetOrdersQuery({
    page: 1,
    limit: 50,
  });

  const [openOrderId, setOpenOrderId] = useState(null);

  const orders = data?.transactions || data?.orders || [];

  // ✅ Only logged-in user's orders
  const myOrders = orders.filter(
    (order) =>
      order?.email === authUser?.email ||
      order?.userId === authUser?.id
  );

  const toggleOrder = (id) => {
    setOpenOrderId((prev) => (prev === id ? null : id));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20 text-red-500">
        Failed to load orders
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold">My Orders</h1>

      {myOrders.length === 0 ? (
        <p className="text-gray-500">
          You have not placed any orders yet.
        </p>
      ) : (
        <div className="space-y-4">
          {myOrders.map((order) => {
            const isOpen = openOrderId === order._id;

            return (
              <Card key={order._id}>
                {/* HEADER */}
                <CardHeader
                  className="cursor-pointer"
                  onClick={() => toggleOrder(order._id)}
                >
                  <CardTitle className="flex justify-between items-center">
                    <div>
                      <p className="font-medium">
                        {order.courseName || "Course Purchase"}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(
                          order.createdAt
                        ).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="flex items-center gap-3">
                      <Badge
                        variant={
                          order.status === "success"
                            ? "success"
                            : order.status === "pending"
                            ? "secondary"
                            : "destructive"
                        }
                      >
                        {order.status?.toUpperCase()}
                      </Badge>
                      <ChevronDown
                        className={`h-5 w-5 transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                  </CardTitle>
                </CardHeader>

                {/* DETAILS */}
                {isOpen && (
                  <CardContent className="space-y-4 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <p>
                        <strong>Amount:</strong> ₹{order.amount}
                      </p>
                      <p>
                        <strong>Transaction ID:</strong>{" "}
                        {order.transactionId}
                      </p>
                      <p>
                        <strong>Payment Method:</strong>{" "}
                        {order.method || "Online"}
                      </p>
                      <p>
                        <strong>Order Date:</strong>{" "}
                        {new Date(
                          order.createdAt
                        ).toLocaleString()}
                      </p>
                    </div>

                    {/* ACTIONS */}
                    <div className="flex flex-wrap gap-3 pt-2">
                      {order.status === "success" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            navigate("/dashboard/support")
                          }
                        >
                          <MessageSquare className="h-4 w-4 mr-2" />
                          Request Refund
                        </Button>
                      )}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyOrdersPage;
